import React, { useEffect, useState } from 'react';
import { Search, Edit, Trash2, Power, AlertTriangle, CheckCircle, X, Link as LinkIcon, Copy, Check, Send, Loader2, DollarSign, Circle, UserCheck, Trash, Plus, Filter, ChevronLeft, ChevronRight, Mail, Clock, Users, MessageSquare, Star } from 'lucide-react';
import { motion } from 'framer-motion';
import api from '../../services/api';
import ClientEditModal from '../../components/ClientEditModal';
import CreditAdjustmentModal from '../../components/Admin/CreditAdjustmentModal';
import SubscriptionEditModal from '../../components/Admin/SubscriptionEditModal';
import AttributionModal from '../../components/Admin/AttributionModal';
import AddSubscriptionModal from '../../components/Admin/AddSubscriptionModal';
import DeleteSubscriptionModal from '../../components/Admin/DeleteSubscriptionModal';
import PendingValidations from './PendingValidations';
import ReviewsManagement from './ReviewsManagement';
import AdminTickets from './AdminTickets';

const ClientsManagement = () => {
  const [activeTab, setActiveTab] = useState('clients');
  const [clients, setClients] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [subscriptionFilter, setSubscriptionFilter] = useState('all'); // all, active, no_subscription, expiring_soon
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [selectedClient, setSelectedClient] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPaymentLink, setShowPaymentLink] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [showCreditModal, setShowCreditModal] = useState(false);
  const [selectedClientForCredit, setSelectedClientForCredit] = useState(null);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [showAddSubscriptionModal, setShowAddSubscriptionModal] = useState(false);
  const [selectedClientForSubscription, setSelectedClientForSubscription] = useState(null);
  const [showDeleteSubscriptionModal, setShowDeleteSubscriptionModal] = useState(false);
  const [subscriptionToDelete, setSubscriptionToDelete] = useState(null);
  const [showAttributionModal, setShowAttributionModal] = useState(false);
  const [selectedForAttribution, setSelectedForAttribution] = useState(null);
  const [selectedClients, setSelectedClients] = useState(new Set());
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailTarget, setEmailTarget] = useState(null);
  const [emailForm, setEmailForm] = useState({ subject: '', htmlContent: '' });
  const [emailSending, setEmailSending] = useState(false);
  const [emailSuccess, setEmailSuccess] = useState(false);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [promoSending, setPromoSending] = useState(false);
  const [resendingActivation, setResendingActivation] = useState(false);
  const [paymentLinkData, setPaymentLinkData] = useState({
    email: '',
    full_name: '',
    amount: 59,
    plan_name: 'Abonnement IPTV 12 mois',
    vendor_code: '',
    send_email: true
  });
  const [generatedLink, setGeneratedLink] = useState('');
  const [linkCopied, setLinkCopied] = useState(false);
  const [linkLoading, setLinkLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);

  // Server-side pagination state
  const [totalClients, setTotalClients] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Debounced search
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm), 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, subscriptionFilter, itemsPerPage]);

  useEffect(() => {
    fetchClients();
  }, [currentPage, debouncedSearch, subscriptionFilter, itemsPerPage]);

  const fetchClients = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: currentPage,
        per_page: itemsPerPage,
        search: debouncedSearch,
        subscription_filter: subscriptionFilter
      });
      const response = await api.get(`/admin/clients-paginated?${params}`);
      const data = response.data;
      setClients(data.clients || []);
      setTotalClients(data.total || 0);
      setTotalPages(data.total_pages || 0);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const getSubscriptionLabel = (client) => {
    // Check if client has any subscriptions in the subscriptions array
    const subscriptions = client.subscriptions || [];
    
    if (subscriptions.length === 0) {
      return { text: "Pas d'accès", color: 'text-[#FF3B5C]', bg: 'bg-[#FF3B5C]/10' };
    }
    
    // Find the most recent active or pending subscription
    const activeSub = subscriptions.find(s => s.status === 'active');
    const pendingSub = subscriptions.find(s => s.status === 'pending_credentials');
    
    // Priority: active > pending_credentials > expired
    const currentSub = activeSub || pendingSub;
    
    if (!currentSub) {
      // All subscriptions are expired or suspended
      const expiredSub = subscriptions.find(s => s.status === 'expired');
      if (expiredSub) {
        return { text: "Expiré", color: 'text-[#6B6B6E]', bg: 'bg-[#3A3A3D]/10' };
      }
      return { text: "Pas d'accès", color: 'text-[#FF3B5C]', bg: 'bg-[#FF3B5C]/10' };
    }
    
    if (currentSub.status === 'pending_credentials') {
      return { text: "En attente identifiants", color: 'text-[#FFB800]', bg: 'bg-[#FFB800]/10' };
    }
    
    // Active subscription
    const endDate = currentSub.end_date ? new Date(currentSub.end_date).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }) : '';
    
    return { 
      text: `Abonné - ${endDate}`, 
      color: 'text-[#00FF88]', 
      bg: 'bg-[#00FF88]/10' 
    };
  };

  const generatePaymentLink = async () => {
    if (!paymentLinkData.email || !paymentLinkData.full_name) return;
    
    setLinkLoading(true);
    try {
      const response = await api.post('/admin/payment-links', paymentLinkData);
      setGeneratedLink(response.data.payment_url);
      setLinkCopied(false);
    } catch (error) {
      console.error('Error generating payment link:', error);
      alert(error.response?.data?.detail || 'Erreur lors de la génération du lien');
    } finally {
      setLinkLoading(false);
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(generatedLink);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  };

  const toggleClientStatus = async (clientId) => {
    setActionLoading(clientId);
    try {
      await api.put(`/admin/users/${clientId}/toggle-status`);
      fetchClients();
    } catch (error) {
      console.error('Error toggling status:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const deleteClient = async (clientId) => {
    setActionLoading(clientId);
    try {
      await api.delete(`/admin/users/${clientId}`);
      setShowDeleteConfirm(null);
      fetchClients();
    } catch (error) {
      console.error('Error deleting client:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const handleSelectClient = (clientId) => {
    const newSelected = new Set(selectedClients);
    if (newSelected.has(clientId)) {
      newSelected.delete(clientId);
    } else {
      newSelected.add(clientId);
    }
    setSelectedClients(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedClients.size === clients.length) {
      setSelectedClients(new Set());
    } else {
      setSelectedClients(new Set(clients.map(c => c.id)));
    }
  };

  const handleBulkDelete = async () => {
    setActionLoading('bulk');
    try {
      await Promise.all(
        Array.from(selectedClients).map(id => api.delete(`/admin/users/${id}`))
      );
      setShowBulkDeleteConfirm(false);
      setSelectedClients(new Set());
      fetchClients();
      alert(`${selectedClients.size} clients supprimés avec succès`);
    } catch (error) {
      console.error('Error bulk deleting:', error);
      alert('Erreur lors de la suppression en masse');
    } finally {
      setActionLoading(null);
    }
  };

  const getRiskColor = (score) => {
    if (score >= 70) return 'text-[#FF3B5C] bg-[#FF3B5C]/10';
    if (score >= 40) return 'text-[#FFB800] bg-[#FFB800]/10';
    return 'text-[#00FF88] bg-[#00FF88]/10';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  const resetPaymentLinkModal = () => {
    setShowPaymentLink(false);
    setGeneratedLink('');
    setPaymentLinkData({
      email: '',
      full_name: '',
      amount: 59,
      plan_name: 'Abonnement IPTV 12 mois',
      vendor_code: '',
      send_email: true
    });
  };

  const openSubscriptionModal = (client, subscription) => {
    setSelectedClient(client);
    setSelectedSubscription(subscription);
    setShowSubscriptionModal(true);
  };

  // Composant Icône d'état abonnement
  const SubscriptionStatusIcon = ({ subscription }) => {
    const getIconConfig = () => {
      switch (subscription.status_icon) {
        case 'green':
          return {
            color: 'text-[#00FF88]',
            bgColor: 'bg-[#00FF88]',
            tooltip: `Actif - Expire le ${new Date(subscription.end_date).toLocaleDateString('fr-FR')}`
          };
        case 'orange':
          return {
            color: 'text-[#FFB800]',
            bgColor: 'bg-[#FFB800]',
            tooltip: `Expire dans ${subscription.days_remaining} jours`
          };
        case 'red':
          return {
            color: 'text-[#FF3B5C]',
            bgColor: 'bg-[#FF3B5C]',
            tooltip: subscription.end_date ? `Expiré le ${new Date(subscription.end_date).toLocaleDateString('fr-FR')}` : 'Expiré'
          };
        case 'yellow':
          return {
            color: 'text-[#FFB800]',
            bgColor: 'bg-[#FFB800]',
            tooltip: 'En attente identifiants'
          };
        default:
          return {
            color: 'text-[#6B6B6E]',
            bgColor: 'bg-[#6B6B6E]',
            tooltip: 'Statut inconnu'
          };
      }
    };

    const { color, bgColor, tooltip } = getIconConfig();

    return (
      <button
        onClick={() => openSubscriptionModal(subscription._client, subscription)}
        className={`p-1.5 rounded-full ${bgColor}/20 hover:${bgColor}/40 transition-all hover:scale-110`}
        title={tooltip}
      >
        <Circle className={`h-3.5 w-3.5 ${color} fill-current`} />
      </button>
    );
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8" data-testid="clients-management">
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2">Gestion Clients</h1>
        <p className="text-[#B8B8BA] text-sm">Vue d&apos;ensemble et administration des clients</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-[#121214] p-1 rounded-lg border border-[#3A3A3D]/40 w-fit overflow-x-auto" data-testid="client-tabs">
        <button onClick={() => setActiveTab('clients')}
          className={`flex items-center gap-2 px-4 py-2 rounded text-sm font-medium whitespace-nowrap transition-all ${activeTab === 'clients' ? 'bg-[#FF6B35] text-black' : 'text-[#B8B8BA] hover:text-white hover:bg-[#1A1A1D]'}`} data-testid="tab-clients">
          <Users className="h-4 w-4" />Clients
        </button>
        <button onClick={() => setActiveTab('validations')}
          className={`flex items-center gap-2 px-4 py-2 rounded text-sm font-medium whitespace-nowrap transition-all ${activeTab === 'validations' ? 'bg-[#FF6B35] text-black' : 'text-[#B8B8BA] hover:text-white hover:bg-[#1A1A1D]'}`} data-testid="tab-validations">
          <Clock className="h-4 w-4" />Validations
        </button>
        <button onClick={() => setActiveTab('tickets')}
          className={`flex items-center gap-2 px-4 py-2 rounded text-sm font-medium whitespace-nowrap transition-all ${activeTab === 'tickets' ? 'bg-[#FF6B35] text-black' : 'text-[#B8B8BA] hover:text-white hover:bg-[#1A1A1D]'}`} data-testid="tab-tickets">
          <MessageSquare className="h-4 w-4" />Tickets
        </button>
        <button onClick={() => setActiveTab('reviews')}
          className={`flex items-center gap-2 px-4 py-2 rounded text-sm font-medium whitespace-nowrap transition-all ${activeTab === 'reviews' ? 'bg-[#FF6B35] text-black' : 'text-[#B8B8BA] hover:text-white hover:bg-[#1A1A1D]'}`} data-testid="tab-reviews">
          <Star className="h-4 w-4" />Avis
        </button>
      </div>

      {activeTab === 'validations' ? (
        <PendingValidations />
      ) : activeTab === 'tickets' ? (
        <AdminTickets />
      ) : activeTab === 'reviews' ? (
        <ReviewsManagement />
      ) : (
      <>
      <div className="mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
          <div className="hidden sm:block"></div>
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 w-full sm:w-auto">
            {selectedClients.size > 0 && (
              <button
                onClick={() => setShowBulkDeleteConfirm(true)}
                className="bg-[#FF3B5C] hover:bg-[#FF2244] text-white px-4 py-2 rounded-lg flex items-center justify-center space-x-2 transition-colors w-full sm:w-auto"
                data-testid="bulk-delete-btn"
              >
                <Trash className="h-5 w-5" />
                <span>Supprimer ({selectedClients.size})</span>
              </button>
            )}
            <button
              onClick={() => setShowPaymentLink(true)}
              className="btn-primary cyber-glow-hover flex items-center justify-center space-x-2 w-full sm:w-auto"
              data-testid="generate-payment-link-btn"
            >
              <LinkIcon className="h-5 w-5" />
              <span>Générer lien de paiement</span>
            </button>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center">
          {/* Barre de recherche */}
          <div className="relative flex-1 w-full lg:max-w-md">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-[#B8B8BA]" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Rechercher par nom ou email..."
              className="w-full bg-[#1A1A1D] border border-[#3A3A3D] rounded-lg pl-12 pr-4 py-3 text-white focus:border-[#00FFFF] focus:outline-none"
              data-testid="search-clients-input"
            />
          </div>

          {/* Filtres */}
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-[#B8B8BA]" />
              <span className="text-sm text-[#B8B8BA]">Filtrer :</span>
            </div>
            <select
              value={subscriptionFilter}
              onChange={(e) => setSubscriptionFilter(e.target.value)}
              className="bg-[#1A1A1D] border border-[#3A3A3D] rounded-lg px-3 py-2 text-white text-sm focus:border-[#00FFFF] focus:outline-none"
              data-testid="subscription-filter"
            >
              <option value="all">Tous les clients</option>
              <option value="active">Avec abonnement actif</option>
              <option value="no_subscription">Sans abonnement</option>
              <option value="expiring_soon">Expire dans - de 30 jours</option>
            </select>

            <select
              value={itemsPerPage}
              onChange={(e) => setItemsPerPage(Number(e.target.value))}
              className="bg-[#1A1A1D] border border-[#3A3A3D] rounded-lg px-3 py-2 text-white text-sm focus:border-[#00FFFF] focus:outline-none"
              data-testid="items-per-page"
            >
              <option value={10}>10 par page</option>
              <option value={20}>20 par page</option>
              <option value={50}>50 par page</option>
              <option value={100}>100 par page</option>
            </select>
          </div>
        </div>

        {/* Résumé des résultats */}
        <div className="mt-4 text-sm text-[#B8B8BA]">
        {totalClients} client{totalClients > 1 ? 's' : ''} trouvé{totalClients > 1 ? 's' : ''}
          {subscriptionFilter !== 'all' && (
            <span className="ml-2 px-2 py-0.5 bg-[#FF6B35]/20 text-[#FF6B35] rounded-full text-xs">
              Filtre actif
            </span>
          )}
        </div>
      </div>

      <div className="bg-[#1A1A1D] rounded-lg metallic-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1000px]">
            <thead className="bg-[#222226]">
              <tr>
                <th className="px-3 py-3 text-center text-[#B8B8BA] font-semibold text-sm whitespace-nowrap w-12">
                  <input
                    type="checkbox"
                    checked={selectedClients.size === clients.length && clients.length > 0}
                    onChange={handleSelectAll}
                    className="w-4 h-4 rounded border-[#3A3A3D] bg-[#222226] text-[#00FFFF] focus:ring-[#00FFFF]"
                  />
                </th>
                <th className="px-4 py-3 text-left text-[#B8B8BA] font-semibold text-sm whitespace-nowrap">Client</th>
                <th className="px-4 py-3 text-left text-[#B8B8BA] font-semibold text-sm whitespace-nowrap">Email</th>
                <th className="px-3 py-3 text-left text-[#B8B8BA] font-semibold text-sm whitespace-nowrap w-24">Vendeur</th>
                <th className="px-2 py-3 text-left text-[#B8B8BA] font-semibold text-sm whitespace-nowrap w-20">Parrain</th>
                <th className="px-3 py-3 text-left text-[#B8B8BA] font-semibold text-sm whitespace-nowrap">Crédits</th>
                <th className="px-3 py-3 text-center text-[#B8B8BA] font-semibold text-sm whitespace-nowrap">Filleuls</th>
                <th className="px-3 py-3 text-center text-[#B8B8BA] font-semibold text-sm whitespace-nowrap">Risque</th>
                <th className="px-3 py-3 text-center text-[#B8B8BA] font-semibold text-sm whitespace-nowrap">Abonnements</th>
                <th className="px-3 py-3 text-center text-[#B8B8BA] font-semibold text-sm whitespace-nowrap">Compte</th>
                <th className="px-3 py-3 text-center text-[#B8B8BA] font-semibold text-sm whitespace-nowrap">Actions</th>
              </tr>
            </thead>
            <tbody>
              {clients.map((client, index) => {
                return (
                <tr key={client.id} className="border-t border-[#3A3A3D] hover:bg-[#222226] transition-colors" data-testid={`client-row-${index}`}>
                  <td className="px-3 py-3 text-center">
                    <input
                      type="checkbox"
                      checked={selectedClients.has(client.id)}
                      onChange={() => handleSelectClient(client.id)}
                      className="w-4 h-4 rounded border-[#3A3A3D] bg-[#222226] text-[#00FFFF] focus:ring-[#00FFFF]"
                    />
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-white font-semibold text-sm truncate max-w-[120px]" title={client.full_name}>{client.full_name}</div>
                    {client.snapchat && (
                      <div className="text-[#FFFC00] text-xs truncate max-w-[120px]" title={client.snapchat}>@{client.snapchat}</div>
                    )}
                    <div className="text-[#6B6B6E] text-xs">ID: {client.id.slice(-8)}</div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-[#B8B8BA] text-sm truncate block max-w-[160px]" title={client.email}>{client.email}</span>
                  </td>
                  <td className="px-3 py-3">
                    {client.vendor ? (
                      <div className="max-w-[100px]">
                        <div className="text-[#FFB800] font-medium text-xs truncate" title={client.vendor.full_name}>
                          {client.vendor.full_name}
                        </div>
                        <div className="text-[#6B6B6E] text-[10px] truncate" title={client.vendor.email}>
                          {client.vendor.email}
                        </div>
                      </div>
                    ) : (
                      <button
                        onClick={() => {
                          setSelectedForAttribution(client);
                          setShowAttributionModal(true);
                        }}
                        className="text-[#6B6B6E] hover:text-[#00FFFF] text-xs flex items-center space-x-1"
                        title="Attribuer un vendeur"
                      >
                        <UserCheck className="h-3 w-3" />
                        <span>Direct</span>
                      </button>
                    )}
                  </td>
                  <td className="px-2 py-3">
                    {client.referrer ? (
                      <div className="max-w-[90px]">
                        <div className="text-[#00FF88] font-medium text-xs truncate" title={client.referrer.full_name}>
                          {client.referrer.full_name}
                        </div>
                        <div className="text-[#6B6B6E] text-[10px] truncate" title={client.referrer.email}>
                          {client.referrer.email}
                        </div>
                      </div>
                    ) : (
                      <button
                        onClick={() => {
                          setSelectedForAttribution(client);
                          setShowAttributionModal(true);
                        }}
                        className="text-[#6B6B6E] hover:text-[#00FFFF] text-xs flex items-center space-x-1"
                        title="Attribuer un parrain"
                      >
                        <UserCheck className="h-3 w-3" />
                        <span>-</span>
                      </button>
                    )}
                  </td>
                  <td className="px-3 py-3 text-sm whitespace-nowrap">
                    <div className="space-y-1">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-[#6B6B6E] text-xs">Disponible:</span>
                        <span className="text-[#00FF88] font-bold">{client.credit_available?.toFixed(2) || '0.00'}€</span>
                      </div>
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-[#6B6B6E] text-xs">En attente:</span>
                        <span className="text-[#FFB800] font-bold">{client.credit_pending?.toFixed(2) || '0.00'}€</span>
                      </div>
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-[#6B6B6E] text-xs">Total:</span>
                        <span className="text-[#00FFFF] font-bold">{client.total_credit_earned?.toFixed(2) || '0.00'}€</span>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-3 text-white text-sm text-center">{client.referral_count || 0}</td>
                  <td className="px-3 py-3 text-center">
                    <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getRiskColor(client.risk_score || 0)}`}>
                      {(client.risk_score || 0) < 40 ? 'Faible' : (client.risk_score || 0) < 70 ? 'Moyen' : 'Élevé'}
                    </span>
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex items-center justify-center space-x-1.5">
                      {client.subscriptions && client.subscriptions.length > 0 ? (
                        client.subscriptions.map((sub) => {
                          // Ajouter référence au client pour la modale
                          const subWithClient = { ...sub, _client: client };
                          
                          const getIconConfig = () => {
                            switch (sub.status_icon) {
                              case 'green':
                                return { color: 'text-[#00FF88]', bgColor: 'bg-[#00FF88]' };
                              case 'orange':
                                return { color: 'text-[#FFB800]', bgColor: 'bg-[#FFB800]' };
                              case 'red':
                                return { color: 'text-[#FF3B5C]', bgColor: 'bg-[#FF3B5C]' };
                              case 'yellow':
                                return { color: 'text-[#FFB800]', bgColor: 'bg-[#FFB800]' };
                              default:
                                return { color: 'text-[#6B6B6E]', bgColor: 'bg-[#6B6B6E]' };
                            }
                          };
                          
                          const { color, bgColor } = getIconConfig();
                          
                          const getTooltip = () => {
                            if (sub.status === 'pending_credentials') return 'En attente identifiants';
                            if (sub.status === 'expired') return 'Expiré';
                            if (sub.days_remaining !== null && sub.days_remaining <= 30 && sub.days_remaining > 0) {
                              return `Expire dans ${sub.days_remaining} jours`;
                            }
                            if (sub.end_date) return `Actif - ${new Date(sub.end_date).toLocaleDateString('fr-FR')}`;
                            return 'Actif';
                          };
                          
                          return (
                            <button
                              key={sub.id}
                              onClick={() => openSubscriptionModal(client, sub)}
                              className={`p-1.5 rounded-full ${bgColor}/20 hover:${bgColor}/40 transition-all hover:scale-110`}
                              title={getTooltip()}
                              data-testid={`open-subscription-${sub.id}`}
                            >
                              <Circle className={`h-3.5 w-3.5 ${color} fill-current`} />
                            </button>
                          );
                        })
                      ) : (
                        <span className="text-[#6B6B6E] text-xs">Aucun</span>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-3 text-center">
                    <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                      client.is_active ? 'bg-[#00FFFF]/10 text-[#00FFFF]' : 'bg-[#FF3B5C]/10 text-[#FF3B5C]'
                    }`}>
                      {client.is_active ? 'Actif' : 'Inactif'}
                    </span>
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex items-center justify-center space-x-1">
                      <button
                        onClick={() => {
                          setSelectedClient(client);
                          setShowEditModal(true);
                        }}
                        className="text-[#00FFFF] hover:text-[#00E5E5] p-1"
                        title="Modifier"
                        data-testid={`edit-client-${index}`}
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => {
                          setSelectedClientForSubscription(client);
                          setShowAddSubscriptionModal(true);
                        }}
                        className="text-[#00FF88] hover:text-[#00E577] p-1"
                        title="Ajouter un abonnement"
                      >
                        <Plus className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => {
                          setSelectedForAttribution(client);
                          setShowAttributionModal(true);
                        }}
                        className="text-[#FFB800] hover:text-[#FFA000] p-1"
                        title="Attribuer vendeur/parrain"
                        data-testid={`attribution-client-${index}`}
                      >
                        <UserCheck className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => {
                          setSelectedClientForCredit(client);
                          setShowCreditModal(true);
                        }}
                        className="text-[#FFB800] hover:text-[#FFA000] p-1"
                        title="Ajuster crédit"
                        data-testid={`credit-client-${index}`}
                      >
                        <DollarSign className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => {
                          setEmailTarget(client);
                          setEmailForm({ subject: '', htmlContent: '' });
                          setEmailSuccess(false);
                          setShowEmailModal(true);
                          api.get('/admin/email-templates').then(res => setEmailTemplates(res.data || [])).catch(() => {});
                        }}
                        className="text-[#F7931E] hover:text-[#FF6B35] p-1"
                        title="Envoyer un email"
                        data-testid={`email-client-${index}`}
                      >
                        <Mail className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => toggleClientStatus(client.id)}
                        disabled={actionLoading === client.id}
                        className={`p-1 ${client.is_active ? 'text-[#FFB800] hover:text-[#FFA000]' : 'text-[#00FF88] hover:text-[#00E577]'}`}
                        title={client.is_active ? 'Désactiver' : 'Activer'}
                        data-testid={`toggle-client-${index}`}
                      >
                        <Power className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => setShowDeleteConfirm(client)}
                        className="text-[#FF3B5C] hover:text-[#FF2244] p-1"
                        title="Supprimer"
                        data-testid={`delete-client-${index}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="flex flex-wrap items-center justify-between gap-3 mt-4 px-2 sm:px-4" data-testid="pagination-controls">
          <div className="text-sm text-[#B8B8BA]">
            Page {currentPage} sur {totalPages}
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="p-2 rounded-lg bg-[#1A1A1D] border border-[#3A3A3D] text-white hover:border-[#00FFFF] disabled:opacity-30 disabled:hover:border-[#3A3A3D] transition-colors"
              data-testid="pagination-prev"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }
              return (
                <button
                  key={pageNum}
                  onClick={() => setCurrentPage(pageNum)}
                  className={`w-8 h-8 rounded-lg text-sm font-medium transition-colors ${
                    currentPage === pageNum
                      ? 'bg-[#FF6B35] text-white'
                      : 'bg-[#1A1A1D] border border-[#3A3A3D] text-[#B8B8BA] hover:border-[#00FFFF]'
                  }`}
                  data-testid={`pagination-page-${pageNum}`}
                >
                  {pageNum}
                </button>
              );
            })}
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="p-2 rounded-lg bg-[#1A1A1D] border border-[#3A3A3D] text-white hover:border-[#00FFFF] disabled:opacity-30 disabled:hover:border-[#3A3A3D] transition-colors"
              data-testid="pagination-next"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {showEditModal && selectedClient && (
        <ClientEditModal
          client={selectedClient}
          onClose={() => {
            setShowEditModal(false);
            setSelectedClient(null);
            fetchClients();
          }}
          onSave={() => fetchClients()}
          isAdmin={true}
        />
      )}

      {/* Payment Link Modal - Fixed for single plan + custom amount */}
      {showPaymentLink && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center" onClick={resetPaymentLinkModal}>
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-[#1A1A1D] p-8 rounded-lg metallic-border cyber-glow max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto"
            data-testid="payment-link-modal"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white">Générer un lien de paiement</h2>
              <button onClick={resetPaymentLinkModal} className="text-[#B8B8BA] hover:text-white">
                <X className="h-6 w-6" />
              </button>
            </div>

            {!generatedLink ? (
              <div className="space-y-4">
                <div>
                  <label className="block text-[#E8E8E8] mb-2 text-sm">Nom complet *</label>
                  <input
                    type="text"
                    value={paymentLinkData.full_name}
                    onChange={(e) => setPaymentLinkData({...paymentLinkData, full_name: e.target.value})}
                    className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg px-4 py-3 text-white focus:border-[#00FFFF] focus:outline-none"
                    placeholder="Jean Dupont"
                  />
                </div>
                
                <div>
                  <label className="block text-[#E8E8E8] mb-2 text-sm">Email *</label>
                  <input
                    type="email"
                    value={paymentLinkData.email}
                    onChange={(e) => setPaymentLinkData({...paymentLinkData, email: e.target.value})}
                    className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg px-4 py-3 text-white focus:border-[#00FFFF] focus:outline-none"
                    placeholder="client@example.com"
                    data-testid="payment-email-input"
                  />
                </div>

                {/* Fixed Plan Display */}
                <div className="bg-[#222226] p-4 rounded-lg border border-[#3A3A3D]">
                  <div className="flex justify-between items-center mb-3">
                    <div>
                      <span className="text-[#B8B8BA] text-sm">Offre</span>
                      <div className="text-white font-bold">Abonnement IPTV 12 mois</div>
                    </div>
                    <div className="text-right">
                      <span className="text-[#6B6B6E] text-sm">Prix standard</span>
                      <div className="text-[#00FFFF] font-bold text-xl">59€</div>
                    </div>
                  </div>
                </div>

                {/* Custom Amount */}
                <div>
                  <label className="block text-[#E8E8E8] mb-2 text-sm">Montant personnalisé (€)</label>
                  <input
                    type="number"
                    value={paymentLinkData.amount}
                    onChange={(e) => setPaymentLinkData({...paymentLinkData, amount: parseFloat(e.target.value) || 0})}
                    className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg px-4 py-3 text-white focus:border-[#00FFFF] focus:outline-none"
                    min="0"
                    step="0.01"
                  />
                  <p className="text-[#6B6B6E] text-xs mt-1">Modifiez le montant si vous souhaitez appliquer une réduction spéciale</p>
                </div>

                <div>
                  <label className="block text-[#E8E8E8] mb-2 text-sm">Code vendeur (optionnel)</label>
                  <input
                    type="text"
                    value={paymentLinkData.vendor_code}
                    onChange={(e) => setPaymentLinkData({...paymentLinkData, vendor_code: e.target.value})}
                    className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg px-4 py-3 text-white focus:border-[#00FFFF] focus:outline-none"
                    placeholder="CODE123"
                  />
                  <p className="text-[#6B6B6E] text-xs mt-1">Attribuer cette vente à un vendeur</p>
                </div>

                {/* Send Email Toggle */}
                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    id="send_email"
                    checked={paymentLinkData.send_email}
                    onChange={(e) => setPaymentLinkData({...paymentLinkData, send_email: e.target.checked})}
                    className="w-5 h-5 rounded border-[#3A3A3D] bg-[#222226] text-[#00FFFF] focus:ring-[#00FFFF]"
                  />
                  <label htmlFor="send_email" className="text-[#B8B8BA] text-sm">Envoyer le lien par email au client</label>
                </div>

                <button
                  onClick={generatePaymentLink}
                  disabled={linkLoading || !paymentLinkData.email || !paymentLinkData.full_name}
                  className="btn-primary w-full disabled:opacity-50 flex items-center justify-center space-x-2"
                  data-testid="generate-link-btn"
                >
                  {linkLoading ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin" />
                      <span>Génération...</span>
                    </>
                  ) : (
                    <>
                      <Send className="h-5 w-5" />
                      <span>Générer le lien ({paymentLinkData.amount}€)</span>
                    </>
                  )}
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="bg-[#00FF88]/10 border border-[#00FF88]/30 p-4 rounded-lg flex items-center space-x-3">
                  <CheckCircle className="h-6 w-6 text-[#00FF88]" />
                  <div>
                    <p className="text-[#00FF88] font-medium">Lien créé avec succès !</p>
                    {paymentLinkData.send_email && (
                      <p className="text-[#B8B8BA] text-sm">Un email a été envoyé à {paymentLinkData.email}</p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-[#B8B8BA] text-sm mb-2">Lien de paiement</label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="text"
                      value={generatedLink}
                      readOnly
                      className="flex-1 bg-[#222226] border border-[#3A3A3D] rounded px-3 py-2 text-white text-sm font-mono"
                    />
                    <button onClick={copyLink} className="btn-secondary py-2 px-4 flex items-center space-x-1">
                      {linkCopied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                      <span>{linkCopied ? 'Copié!' : 'Copier'}</span>
                    </button>
                  </div>
                </div>

                <div className="flex space-x-4">
                  <button
                    onClick={() => {
                      setGeneratedLink('');
                      setPaymentLinkData({...paymentLinkData, email: '', full_name: ''});
                    }}
                    className="flex-1 btn-secondary"
                  >
                    Créer un autre lien
                  </button>
                  <button onClick={resetPaymentLinkModal} className="flex-1 btn-primary">
                    Fermer
                  </button>
                </div>
              </div>
            )}
          </motion.div>
        </div>
      )}

      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center" onClick={() => setShowDeleteConfirm(null)}>
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-[#1A1A1D] p-8 rounded-lg metallic-border max-w-md w-full mx-4"
            data-testid="delete-confirm-modal"
          >
            <div className="text-center">
              <div className="inline-block p-4 bg-[#FF3B5C]/10 rounded-full mb-4">
                <AlertTriangle className="h-12 w-12 text-[#FF3B5C]" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-2">Supprimer ce client ?</h3>
              <p className="text-[#B8B8BA] mb-6">
                Cette action est <span className="text-[#FF3B5C] font-bold">irréversible</span>. 
                Le compte de <span className="text-white font-semibold">{showDeleteConfirm.full_name}</span> et toutes ses données seront supprimés définitivement.
              </p>
              <div className="flex space-x-4">
                <button
                  onClick={() => setShowDeleteConfirm(null)}
                  className="flex-1 bg-[#3A3A3D] text-white py-3 rounded-lg hover:bg-[#4A4A4D] transition-colors"
                >
                  Annuler
                </button>
                <button
                  onClick={() => deleteClient(showDeleteConfirm.id)}
                  disabled={actionLoading === showDeleteConfirm.id}
                  className="flex-1 bg-[#FF3B5C] text-white py-3 rounded-lg hover:bg-[#FF2244] transition-colors disabled:opacity-50"
                  data-testid="confirm-delete-btn"
                >
                  {actionLoading === showDeleteConfirm.id ? 'Suppression...' : 'Supprimer'}
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {showCreditModal && selectedClientForCredit && (
        <CreditAdjustmentModal
          client={selectedClientForCredit}
          onClose={() => {
            setShowCreditModal(false);
            setSelectedClientForCredit(null);
          }}
          onSuccess={() => fetchClients()}
        />
      )}

      {showSubscriptionModal && selectedClient && selectedSubscription && (
        <SubscriptionEditModal
          client={selectedClient}
          subscription={selectedSubscription}
          onClose={() => {
            setShowSubscriptionModal(false);
            setSelectedClient(null);
            setSelectedSubscription(null);
          }}
          onSuccess={() => {
            fetchClients();
            setShowSubscriptionModal(false);
          }}
          onDelete={(sub) => {
            setSubscriptionToDelete({ subscription: sub, client: selectedClient });
            setShowDeleteSubscriptionModal(true);
            setShowSubscriptionModal(false);
          }}
        />
      )}

      {showAddSubscriptionModal && selectedClientForSubscription && (
        <AddSubscriptionModal
          client={selectedClientForSubscription}
          onClose={() => {
            setShowAddSubscriptionModal(false);
            setSelectedClientForSubscription(null);
          }}
          onSuccess={fetchClients}
        />
      )}

      {showDeleteSubscriptionModal && subscriptionToDelete && (
        <DeleteSubscriptionModal
          subscription={subscriptionToDelete.subscription}
          client={subscriptionToDelete.client}
          onClose={() => {
            setShowDeleteSubscriptionModal(false);
            setSubscriptionToDelete(null);
          }}
          onSuccess={fetchClients}
        />
      )}

      {showAttributionModal && selectedForAttribution && (
        <AttributionModal
          client={selectedForAttribution}
          onClose={() => {
            setShowAttributionModal(false);
            setSelectedForAttribution(null);
          }}
          onSuccess={() => {
            fetchClients();
            setShowAttributionModal(false);
            setSelectedForAttribution(null);
          }}
        />
      )}

      {showBulkDeleteConfirm && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-[#1A1A1D] p-8 rounded-lg metallic-border max-w-md w-full"
          >
            <div className="text-center">
              <div className="inline-block p-4 bg-[#FF3B5C]/10 rounded-full mb-4">
                <AlertTriangle className="h-12 w-12 text-[#FF3B5C]" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-2">Supprimer {selectedClients.size} clients ?</h3>
              <p className="text-[#B8B8BA] mb-6">
                Cette action est <span className="text-[#FF3B5C] font-bold">irréversible</span>. 
                Les comptes et toutes leurs données seront supprimés définitivement.
              </p>
              <div className="flex space-x-4">
                <button
                  onClick={() => setShowBulkDeleteConfirm(false)}
                  className="flex-1 bg-[#3A3A3D] text-white py-3 rounded-lg hover:bg-[#4A4A4D] transition-colors"
                >
                  Annuler
                </button>
                <button
                  onClick={handleBulkDelete}
                  disabled={actionLoading === 'bulk'}
                  className="flex-1 bg-[#FF3B5C] text-white py-3 rounded-lg hover:bg-[#FF2244] transition-colors disabled:opacity-50"
                >
                  {actionLoading === 'bulk' ? 'Suppression...' : 'Supprimer tout'}
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Email Modal */}
      {showEmailModal && emailTarget && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center" onClick={() => setShowEmailModal(false)}>
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-[#1A1A1D] p-8 rounded-lg metallic-border max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto"
            data-testid="email-client-modal"
          >
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold text-white">Envoyer un email</h2>
                <p className="text-[#B8B8BA] text-sm mt-1">{emailTarget.full_name} ({emailTarget.email})</p>
                <div className="flex items-center gap-2 mt-1.5">
                  {emailTarget.is_verified ? (
                    <span className="flex items-center gap-1 text-[#00FF88] text-xs font-medium" data-testid="email-verified-badge">
                      <CheckCircle className="h-3.5 w-3.5" />
                      Email activé
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 text-[#FFB800] text-xs font-medium" data-testid="email-not-verified-badge">
                      <Clock className="h-3.5 w-3.5" />
                      Email non activé
                    </span>
                  )}
                  {!emailTarget.is_verified && (
                    <button
                      onClick={async () => {
                        setResendingActivation(true);
                        try {
                          await api.post('/admin/resend-activation-email', { email: emailTarget.email });
                          setEmailSuccess(true);
                          setTimeout(() => setEmailSuccess(false), 3000);
                        } catch (e) { alert(e.response?.data?.detail || "Erreur lors du renvoi"); }
                        finally { setResendingActivation(false); }
                      }}
                      disabled={resendingActivation}
                      className="px-2.5 py-1 bg-[#FFB800]/20 border border-[#FFB800]/40 text-[#FFB800] text-xs font-medium rounded-md hover:bg-[#FFB800]/30 transition-colors disabled:opacity-50 flex items-center gap-1"
                      data-testid="resend-activation-email-btn"
                    >
                      {resendingActivation ? <Loader2 className="h-3 w-3 animate-spin" /> : <Mail className="h-3 w-3" />}
                      Renvoyer l'activation
                    </button>
                  )}
                </div>
              </div>
              <button onClick={() => setShowEmailModal(false)} className="text-[#B8B8BA] hover:text-white">
                <X className="h-6 w-6" />
              </button>
            </div>

            {emailSuccess && (
              <div className="bg-[#00FF88]/10 border border-[#00FF88] rounded-lg p-3 mb-4 flex items-center space-x-2">
                <CheckCircle className="h-4 w-4 text-[#00FF88]" />
                <span className="text-[#00FF88] text-sm">Email envoyé avec succès !</span>
              </div>
            )}

            {/* Quick Templates */}
            <div className="mb-6">
              <p className="text-[#B8B8BA] text-sm mb-3">Templates rapides :</p>
              <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto">
                {emailTemplates.map((tpl) => (
                  <button
                    key={tpl.id}
                    onClick={() => setEmailForm({ subject: tpl.subject, htmlContent: tpl.content })}
                    className="text-left bg-[#222226] p-3 rounded-lg border border-[#3A3A3D] hover:border-[#FF6B35]/50 transition-colors"
                    data-testid={`email-template-${tpl.id}`}
                  >
                    <span className="text-white text-sm font-medium">{tpl.name}</span>
                  </button>
                ))}
                {emailTemplates.length === 0 && (
                  <p className="text-[#6B6B6E] text-sm col-span-2 text-center py-2">Aucun template</p>
                )}
              </div>
            </div>

            {/* Offre agressive avec code promo */}
            <div className="mb-6 bg-[#FF3B5C]/10 border border-[#FF3B5C]/30 p-4 rounded-lg">
              <p className="text-[#FF3B5C] font-bold text-sm mb-2">Offre agressive (code promo 10EUR - 24h)</p>
              <p className="text-[#B8B8BA] text-xs mb-3">Envoie un email urgent avec essai gratuit + code promo unique de 10EUR valable 24h.</p>
              <button
                onClick={async () => {
                  setPromoSending(true);
                  try {
                    await api.post('/admin/send-promo-email', { email: emailTarget.email, full_name: emailTarget.full_name });
                    setEmailSuccess(true);
                    setTimeout(() => setEmailSuccess(false), 3000);
                  } catch (e) { alert(e.response?.data?.detail || 'Erreur'); }
                  finally { setPromoSending(false); }
                }}
                disabled={promoSending}
                className="px-4 py-2 bg-[#FF3B5C] text-white font-bold rounded-lg hover:bg-[#FF3B5C]/80 transition-colors disabled:opacity-50 text-sm flex items-center gap-2"
                data-testid="send-promo-email-btn"
              >
                {promoSending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                Envoyer l'offre agressive
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-[#E8E8E8] text-sm mb-2">Sujet</label>
                <input
                  type="text"
                  value={emailForm.subject}
                  onChange={(e) => setEmailForm({ ...emailForm, subject: e.target.value })}
                  className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg px-4 py-3 text-white focus:border-[#00FFFF] focus:outline-none"
                  placeholder="Sujet de l'email"
                  data-testid="email-subject-input"
                />
              </div>
              <div>
                <label className="block text-[#E8E8E8] text-sm mb-2">Contenu</label>
                <textarea
                  value={emailForm.htmlContent}
                  onChange={(e) => setEmailForm({ ...emailForm, htmlContent: e.target.value })}
                  rows="8"
                  className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg px-4 py-3 text-white focus:border-[#00FFFF] focus:outline-none font-mono text-sm"
                  placeholder="Contenu HTML de l'email..."
                  data-testid="email-content-input"
                />
              </div>
              <button
                onClick={async () => {
                  setEmailSending(true);
                  try {
                    await api.post('/admin/send-email-to-client', {
                      email: emailTarget.email,
                      subject: emailForm.subject,
                      html_content: emailForm.htmlContent
                    });
                    setEmailSuccess(true);
                    setTimeout(() => setEmailSuccess(false), 3000);
                  } catch (err) {
                    alert(err.response?.data?.detail || "Erreur lors de l'envoi");
                  } finally {
                    setEmailSending(false);
                  }
                }}
                disabled={emailSending || !emailForm.subject || !emailForm.htmlContent}
                className="btn-primary w-full py-3 disabled:opacity-50 flex items-center justify-center space-x-2"
                data-testid="send-client-email-btn"
              >
                <Send className="h-5 w-5" />
                <span>{emailSending ? 'Envoi...' : 'Envoyer'}</span>
              </button>
            </div>
          </motion.div>
        </div>
      )}
      </>
      )}
    </div>
  );
};

export default ClientsManagement;
